import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useEvent } from "../lib/event";
import DashboardLayout from "../components/DashboardLayout";
import { SkeletonRows } from "../components/Skeleton";
import { Search, X } from "lucide-react";
import { notify } from "../lib/notify";

export default function AttendeesPage() {
  const { currentEventId, currentEvent } = useEvent();
  const [data, setData] = useState({ attendees: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [checkin, setCheckin] = useState("");
  const [date, setDate] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const undoCheckin = async (att) => {
    if (!window.confirm(`Undo check-in for ${att.name}? They'll show as not yet arrived and can scan in again.`)) return;
    try {
      await api.post("/checkin/undo", { attendee_id: att.id, device: "Dashboard" });
      notify.success("Check-in undone");
      setReloadKey((k) => k + 1);
    } catch { notify.error("Could not undo check-in"); }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = new URLSearchParams();
      if (q) params.set("q", q); if (checkin) params.set("checkin", checkin);
      if (date) params.set("date", date);
      if (currentEventId) params.set("event_id", currentEventId);
      try {
        const r = await api.get(`/attendees?${params.toString()}`);
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, [q, checkin, date, currentEventId, reloadKey]);

  return (
    <DashboardLayout title="Attendees" subtitle={`${data.total} total${currentEvent ? ` · ${currentEvent.name}` : ""}`}>
      <div className="st-card p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-2.5 text-[#7a7a7a]" size={16} />
          <input className="st-input" style={{ paddingLeft: 40 }} placeholder="Search by name, email, order #"
                 value={q} onChange={(e) => setQ(e.target.value)} data-testid="att-search" />
        </div>
        <select className="st-input max-w-[200px]" value={checkin} onChange={(e) => setCheckin(e.target.value)} data-testid="att-checkin-filter">
          <option value="">All</option>
          <option value="in">Checked in</option>
          <option value="out">Not checked in</option>
        </select>
        <input type="date" className="st-input max-w-[180px]" value={date} onChange={(e) => setDate(e.target.value)} data-testid="att-date" />
      </div>

      <div className="st-card overflow-hidden hidden lg:block">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-wider text-[#7a7a7a]">
            <tr><th className="px-4 py-3">Name</th><th>Email</th><th>Ticket</th><th>Date</th><th>Check-in</th><th>Order</th></tr>
          </thead>
          <tbody data-testid="att-tbody">
            {loading ? (
              <SkeletonRows cols={6} rows={5} />
            ) : data.attendees.length === 0 ? (
              <tr><td colSpan={6} className="py-10 text-center text-[#8a8a8a]">No attendees yet.</td></tr>
            ) : (
              data.attendees.map((a) => (
              <tr key={a.id} className="border-t border-[#1a1a1a] hover:bg-[#0e0e0e]">
                <td className="px-4 py-3">{a.name}</td>
                <td className="text-[#bbb]">{a.email}</td>
                <td>{a.ticket_type_name}</td>
                <td>{a.event_date}</td>
                <td>{a.checked_in ? <button className="st-chip st-chip-green hover:opacity-80" title="Undo this check-in" onClick={() => undoCheckin(a)} data-testid={`undo-checkin-${a.id}`}>Checked in <X size={10} className="inline -mt-0.5" /></button> : <span className="st-chip">—</span>}</td>
                <td><Link to={`/dashboard/orders/${a.order_id}`} className="st-link">View order</Link></td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile — attendee cards */}
      <div className="lg:hidden space-y-3" data-testid="att-mobile-list">
        {loading ? (
          <div className="st-card p-6 text-center text-[#7a7a7a]">Loading…</div>
        ) : data.attendees.length === 0 ? (
          <div className="st-card p-8 text-center text-[#8a8a8a]">No attendees yet.</div>
        ) : (
          data.attendees.map((a) => (
            <div key={a.id} className="st-card p-4" data-testid={`att-card-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{a.name}</div>
                  <div className="text-xs text-[#8a8a8a] truncate">{a.email}</div>
                </div>
                {a.checked_in ? <button className="st-chip st-chip-green shrink-0 hover:opacity-80" title="Undo this check-in" onClick={() => undoCheckin(a)} data-testid={`undo-checkin-${a.id}`}>Checked in <X size={10} className="inline -mt-0.5" /></button> : <span className="st-chip shrink-0">—</span>}
              </div>
              <div className="mt-3 pt-3 border-t border-[#161616] grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-[#7a7a7a]">Ticket</span><div className="text-[#ddd]">{a.ticket_type_name}</div></div>
                <div><span className="text-[#7a7a7a]">Date</span><div className="text-[#ddd]">{a.event_date}</div></div>
              </div>
              <Link to={`/dashboard/orders/${a.order_id}`} className="st-link text-xs mt-3 inline-block">View order</Link>
            </div>
          ))
        )}
      </div>
    </DashboardLayout>
  );
}
